import React, { FC, useState } from 'react';
import { Button, Col, Input, message, Row, Table, Typography } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';

import EnvironmentSelect from '@lib/components/env-select';

import { GauntletPointRowDto } from '@services/_api';
import { exportGauntletPoints, searchGauntletPoints } from '@services/cstool/gauntlet-point';

import UserIdsInput, { parseUserIds } from './components/user-ids-input';

const { Text, Title } = Typography;

const columns = [
  {
    title: 'UserId',
    dataIndex: 'userId',
    key: 'userId',
  },
  {
    title: 'Gauntlet Point',
    dataIndex: 'gauntletPoint',
    key: 'gauntletPoint',
    sorter: (a: GauntletPointRowDto, b: GauntletPointRowDto) =>
      (a.gauntletPoint ?? Number.NEGATIVE_INFINITY) - (b.gauntletPoint ?? Number.NEGATIVE_INFINITY),
    render: (value: number | null) =>
      value === null || value === undefined ? <Text type="secondary">Not found</Text> : value,
  },
];

const GauntletPointScreen: FC = () => {
  const [environment, setEnvironment] = useState<string>();
  const [seasonId, setSeasonId] = useState('');
  const [rawUserIds, setRawUserIds] = useState('');
  const [rows, setRows] = useState<GauntletPointRowDto[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const userIds = parseUserIds(rawUserIds);
  const canSubmit = Boolean(environment && seasonId.trim() && userIds.length);

  const buildBody = () => ({
    environment: environment as string,
    seasonId: seasonId.trim(),
    userIds,
  });

  const handleSearch = async () => {
    if (!canSubmit) return;
    setIsSearching(true);
    const response = await searchGauntletPoints(buildBody()).finally(() => {
      setIsSearching(false);
    });
    if (response.ok) {
      setRows(response.data);
    }
  };

  const handleExport = async () => {
    if (!canSubmit) return;
    setIsExporting(true);
    try {
      await exportGauntletPoints(buildBody());
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 max-h-full min-h-full p-4">
      <Row gutter={[16, 16]} className="mb-4">
        <Col>
          <Title level={5}>Environment</Title>
          <EnvironmentSelect value={environment} style={{ width: 200 }} onChange={setEnvironment} />
        </Col>
        <Col>
          <Title level={5}>Season ID</Title>
          <Input
            value={seasonId}
            placeholder="Enter season id"
            style={{ width: 200 }}
            onChange={(e) => setSeasonId(e.target.value)}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="mb-4">
        <Col flex="auto">
          <Title level={5}>User IDs (comma separated)</Title>
          <UserIdsInput value={rawUserIds} onChange={setRawUserIds} />
        </Col>
      </Row>
      <Row gutter={16} className="mb-4">
        <Col>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={isSearching}
            disabled={!canSubmit}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            loading={isExporting}
            disabled={!canSubmit}
            onClick={handleExport}
          >
            Export Excel
          </Button>
        </Col>
      </Row>
      {rows && (
        <Table
          rowKey="userId"
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
        />
      )}
    </div>
  );
};

export default GauntletPointScreen;
